import { useEffect, useRef, useState } from 'react';
import { Loader2, Send, MessageCircle, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';

// ─────────────────────────────────────────────────────────────────────────────
// WhatsApp chat panel for a single lead. Drops into the right rail of the
// Lead Detail page so the assigned counselor can read + reply without
// leaving the lead view. Polls every 2 seconds for inbound messages so the
// chat feels near-real-time.
//
// Visibility: the backend already restricts this endpoint to the assigned
// counselor / admin / manager, so a 403 response is surfaced as a clear
// "not assigned" hint.
// ─────────────────────────────────────────────────────────────────────────────

interface WhatsAppMessage {
  id: string;
  body: string;
  direction: 'inbound' | 'outbound';
  status: string;
  createdAt: string;
}

interface ChatResponse {
  leadId: string;
  leadName: string;
  phone: string;
  messages: WhatsAppMessage[];
}

// 2s — same cadence as the open thread in the global WhatsApp inbox. The
// per-request cost is tiny (one indexed query) and it makes replies appear
// nearly instantly while the user is staring at the lead detail page.
const POLL_MS = 2000;

export function LeadWhatsAppPanel({ leadId }: { leadId: string }) {
  const [thread, setThread] = useState<ChatResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest message when the thread changes.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages.length]);

  const fetchThread = async () => {
    try {
      const { data } = await api.get(`/whatsapp/lead/${leadId}`);
      setThread(data);
      setError(null);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('Only the assigned counselor (or admin / manager) can chat on this lead.');
      } else if (status === 400) {
        setError(err?.response?.data?.message || 'Lead has no phone number.');
      } else if (status === 404) {
        setError('Lead not found.');
      } else {
        setError('Failed to load WhatsApp messages.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchThread();
    // Lightweight polling — 5s matches notifications poll cadence.
    const timer = setInterval(() => { if (alive) fetchThread(); }, POLL_MS);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Ask Claude to draft a WhatsApp reply from the lead's context, drop it into
  // the composer for the counselor to edit + send. Backend: POST /ai/draft
  // (already exists, plan-feature gated). Read-only — sends nothing.
  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post('/ai/draft', { leadId, type: 'whatsapp' });
      if (data?.text) setBody(data.text);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast('error', typeof msg === 'string' ? msg : 'AI drafting is not available on your plan.');
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    const text = body.trim();
    if (!text) return;

    // Optimistic UI: drop into the thread + clear the textarea BEFORE
    // awaiting the API. Previously the counselor watched their typed
    // message sit there for a beat while the request round-tripped to
    // Meta — felt laggy. The temp row is reconciled by the next 2s poll
    // (or the explicit refetch below).
    const tempId = `temp_${Date.now()}`;
    setThread(t => t ? {
      ...t,
      messages: [
        ...t.messages,
        {
          id: tempId,
          body: text,
          direction: 'outbound',
          status: 'sending',
          createdAt: new Date().toISOString(),
        },
      ],
    } : t);
    setBody('');
    setSending(true);

    try {
      const { data } = await api.post(`/whatsapp/lead/${leadId}/send`, { message: text });
      // Fire-and-forget refresh — the 2s poll will reconcile the temp row
      // with the real server message. NOT awaited so the button re-enables
      // immediately once Meta confirms the send, not after a second round-trip.
      void fetchThread();
      if (data?.templateFallback) {
        toast('warning', '⏰ 24h window closed — an opener template was sent. Your message will deliver once the customer replies.');
      }
    } catch (err: any) {
      // Remove the optimistic row and restore the textarea so the user
      // can edit + retry.
      setThread(t => t ? {
        ...t,
        messages: t.messages.filter(m => m.id !== tempId),
      } : t);
      setBody(text);
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 402 || status === 403) {
        toast('error', typeof msg === 'string' ? msg : 'Your plan does not allow WhatsApp sending. Ask admin to upgrade.');
      } else {
        toast('error', typeof msg === 'string' ? msg : 'Failed to send. Check WhatsApp settings.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          WhatsApp Chat
          {thread?.phone && (
            <span className="text-[11px] font-normal text-slate-400">· {thread.phone}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && !thread ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {error}
          </div>
        ) : (
          <>
            {/* Thread */}
            <div
              ref={scrollRef}
              className="space-y-2 max-h-[360px] overflow-y-auto pr-1"
            >
              {thread!.messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No WhatsApp messages yet. Send a message to start the conversation.
                </p>
              ) : thread!.messages.map(m => {
                const isOut = m.direction === 'outbound';
                return (
                  <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                        isOut
                          ? 'bg-emerald-100 text-emerald-900 rounded-tr-sm'
                          : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <p className="text-[10px] mt-1 opacity-60">
                        {formatDateTime(m.createdAt)}
                        {isOut && (
                          <span className="ml-1">· {m.status}</span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Draft-with-AI — fills the composer with a Claude-drafted reply */}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={draftWithAI}
                disabled={drafting || sending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-100 disabled:opacity-50"
              >
                {drafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Draft with AI
              </button>
            </div>

            {/* Composer */}
            <div className="mt-2 flex items-end gap-2">
              <textarea
                rows={2}
                value={body}
                onChange={e => setBody(e.target.value)}
                onKeyDown={e => {
                  // Cmd/Ctrl + Enter to send, plain Enter inserts newline.
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent placeholder:text-slate-400"
                maxLength={4096}
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !body.trim()}
                className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Tip: ⌘/Ctrl + Enter to send. New messages refresh every 2 seconds.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
